import { zodResolver } from '@hookform/resolvers/zod';
import {
  BillPricingSchema,
  IBillPricing,
  IBillPricingSchema,
} from '@monetize/types/app';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../api/axios';
import {
  useGetConfigurationPricing,
  useUpdateConfigurationPricing,
} from '../../../api/settingsService';
import {
  MAlert,
  MBox,
  MButton,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MHStack,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
} from '../../../components/Monetize';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { SETTINGS } from '../../../constants';
import { useDocumentHead } from '../../../services/documentHead';

const Pricing = () => {
  const { setDocTitle } = useDocumentHead();
  const { data: billPricing, isLoading: isPricingLoading } =
    useGetConfigurationPricing();

  const { isPending: isSaveLoading, mutate: updatePricingMutation } =
    useUpdateConfigurationPricing();

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
  } = useForm<IBillPricingSchema>({
    resolver: zodResolver(BillPricingSchema),
    mode: 'onChange',
    defaultValues: { ...billPricing },
  });

  useEffect(() => {
    if (billPricing) {
      reset({ ...billPricing });
    }
  }, [billPricing]);

  useEffect(() => {
    setDocTitle('Settings', 'Pricing');
  }, []);

  const onSubmit = async (payload: IBillPricing) => {
    updatePricingMutation(payload, {
      onSuccess: () => {},
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });
  };
  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Pricing">
        <MHStack gap="4">
          <MButton
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            isLoading={isSaveLoading}
            isDisabled={isSaveLoading || !isValid || !isDirty}
          >
            Save
          </MButton>
        </MHStack>
      </MSettingsPageHeader>

      {isPricingLoading ? (
        <MFlex justify="center" grow={1} height={'100%'}>
          <MPageLoader height="auto" />
        </MFlex>
      ) : (
        <MBox w="100%" mb="6">
          {billPricing && (
            <MAlert
              type="warning"
              bg="tOrange.input"
              mb={4}
              alertIconProps={{ boxSize: 4 }}
              alertDescriptionProps={{ fontSize: 'sm' }}
            >
              Changing the Proration Model is NOT recommended as it impacts
              Invoices in already Processed Quotes.
            </MAlert>
          )}
          <MBox maxWidth="200">
            <form onSubmit={handleSubmit(onSubmit)}>
              <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
                <MGridItem colSpan={12}>
                  <MFormField
                    error={errors.prorationModel}
                    label="Proration Model"
                    isRequired
                  >
                    <Controller
                      name="prorationModel"
                      control={control}
                      render={({ field }) => (
                        <MCustomSelect
                          items={
                            SETTINGS.SETTING_BILLING_PRICING_PRORATION_MODEL_DISPLAY_STRING
                          }
                          {...field}
                          placeholder="Proration Model"
                        />
                      )}
                    />
                  </MFormField>
                </MGridItem>
              </MGrid>
            </form>
          </MBox>
        </MBox>
      )}
    </MPageContainer>
  );
};

export default Pricing;
